import { useState, useEffect } from "react";
import { storageGet, storageSet, ONBOARDING_DISMISSED_KEY } from "@/lib/uiStorage";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Mic2, Film, ShieldCheck, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Bump this version to re-show the popover to all users after a meaningful release.
const WHATS_NEW_VERSION = "2026-10-10";
const STORAGE_KEY = `lumora_whats_new_seen_${WHATS_NEW_VERSION}`;

const FEATURES = [
  {
    icon: Mic2,
    title: "Talking Creator Ads",
    description: "A creator says your testimonial on camera — lip-synced speech, 720p vertical, ready for TikTok.",
    href: "/studio",
    cta: "Film a creator ad",
    color: "from-violet-600 to-indigo-600",
  },
  {
    icon: Film,
    title: "Slideshow → MP4",
    description: "Stitch scenes, voiceover and burned-in captions into one upload-ready video. No extra credits.",
    href: "/studio",
    cta: "Render an ad",
    color: "from-fuchsia-600 to-purple-600",
  },
  {
    icon: ShieldCheck,
    title: "Claims Compliance Checker",
    description: "Every script is checked against Meta, TikTok, ACCC, TGA and NZ ASA rules before you spend a credit.",
    href: "/studio",
    cta: "Check your copy",
    color: "from-indigo-500 to-violet-600",
  },
];

/** Decides when to show the popover: once per version, never on /admin, and after the onboarding modal. */
function useWhatsNewVisibility(userId, loading, pathname) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (loading || !userId || pathname.startsWith("/admin") || storageGet(STORAGE_KEY) === "1") return;
    const onboardingPending = !storageGet(ONBOARDING_DISMISSED_KEY, { session: true });
    const t = setTimeout(() => {
      // Re-check at fire time — onboarding may have been resolved by now; never stack two modals.
      if (onboardingPending && !storageGet(ONBOARDING_DISMISSED_KEY, { session: true })) return;
      setOpen(true);
    }, onboardingPending ? 6000 : 1200);
    return () => clearTimeout(t);
  }, [userId, loading, pathname]);
  const close = () => { setOpen(false); storageSet(STORAGE_KEY, "1"); };
  return { open, close };
}

const FeatureRow = ({ feature: f, onClick }) => {
  const Icon = f.icon;
  return (
    <Link to={f.href} onClick={onClick} data-testid={`whats-new-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex items-start gap-4 p-4 rounded-2xl bg-white/[0.04] hover:bg-white/[0.07] border border-white/[0.06] hover:border-white/15 transition-all group">
      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform shadow-lg`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-bold text-sm">{f.title}</p>
        <p className="text-white/55 text-xs mt-0.5 leading-relaxed">{f.description}</p>
        <span className="inline-flex items-center gap-1 mt-2 text-purple-300 text-xs font-medium group-hover:text-purple-200">
          {f.cta} <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
};

const PopoverCard = ({ onClose }) => (
  <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
    transition={{ duration: 0.3, ease: "easeOut" }} onClick={(e) => e.stopPropagation()}
    className="relative w-full max-w-lg rounded-3xl p-7 md:p-8 bg-gradient-to-b from-[#1A0F3D] to-[#0F0728] border border-white/10 shadow-2xl shadow-purple-900/40">
    <button type="button" onClick={onClose} aria-label="Close" data-testid="whats-new-close-btn"
      className="absolute top-4 right-4 w-9 h-9 rounded-full text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors">
      <X className="w-4 h-4" />
    </button>
    <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-2 rounded-full bg-gradient-to-r from-violet-600/30 to-indigo-600/30 border border-violet-400/30 text-purple-300 text-[11px] font-bold tracking-wide uppercase">
      <Sparkles className="w-3 h-3" /> What's New
    </span>
    <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-2 leading-tight"><span className="gradient-text">3 powerful upgrades</span> are live</h2>
    <p className="text-white/60 text-sm mb-6">We've shipped tools to help you generate more, scale faster, and stay on-brand.</p>
    <div className="space-y-3">{FEATURES.map((f) => <FeatureRow key={f.title} feature={f} onClick={onClose} />)}</div>
    <button type="button" onClick={onClose} data-testid="whats-new-dismiss-btn"
      className="w-full mt-6 py-3 rounded-full text-white/70 hover:text-white text-sm font-medium transition-colors">Got it, thanks</button>
  </motion.div>
);

export const WhatsNewPopover = () => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const { open, close } = useWhatsNewVisibility(user?.user_id, loading, pathname);
  if (!user) return null;
  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 backdrop-blur-sm bg-black/60" onClick={close} data-testid="whats-new-popover">
          <PopoverCard onClose={close} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
